import React, { useEffect, useState, useCallback } from 'react';
import { contactsService, Contact } from '../lib/db/contacts';
import { customersService } from '../lib/db/customers';
import { PlusIcon, PencilIcon, TrashIcon, UserMinusIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { duplicateContactEmailMessage, findDuplicateContact, contactStatusLabel, isContactActive } from '../utils/contactEmail';

interface ContactWithCustomer extends Contact {
  customers?: {
    company: string;
  };
}

const Contacts: React.FC = () => {
  const [contacts, setContacts] = useState<ContactWithCustomer[]>([]);
  const [customers, setCustomers] = useState<{ id: string; company: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>('');
  const [showInactive, setShowInactive] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: '',
    name: '',
    email: '',
    phone: '',
    position: '',
  });

  const fetchContacts = useCallback(async () => {
    try {
      const [contactsData, customersData] = await Promise.all([
        selectedCustomerId
          ? contactsService.getByCustomerId(selectedCustomerId)
          : contactsService.getAll(),
        customersService.getAll([{ field: 'company', direction: 'asc' }]),
      ]);

      const customerMap = new Map(customersData.map((c) => [c.id, c]));
      const contactsWithCustomers: ContactWithCustomer[] = contactsData.map((contact) => ({
        ...contact,
        customers: customerMap.get(contact.customer_id)
          ? { company: customerMap.get(contact.customer_id)!.company }
          : undefined,
      }));

      setContacts(contactsWithCustomers);
      setCustomers(customersData.map((c) => ({ id: c.id, company: c.company })));
    } catch (error) {
      alert(error instanceof Error ? error.message : '取得聯絡人列表失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [selectedCustomerId]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const duplicate = findDuplicateContact(contacts, formData, editingContact?.id);
    if (duplicate) {
      alert(duplicateContactEmailMessage(duplicate));
      return;
    }
    
    try {
      if (editingContact) {
        await contactsService.update(editingContact.id, formData);
      } else {
        await contactsService.create(formData);
      }

      setShowModal(false);
      setEditingContact(null);
      resetForm();
      fetchContacts();
    } catch (error) {
      alert(error instanceof Error ? error.message : '儲存聯絡人失敗，請稍後再試');
    }
  };

  const handleEdit = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      customer_id: contact.customer_id,
      name: contact.name,
      email: contact.email || '',
      phone: contact.phone || '',
      position: contact.position || '',
    });
    setShowModal(true);
  };

  const handleSetStatus = async (contact: Contact, status: 'active' | 'inactive') => {
    const confirmed = window.confirm(
      status === 'inactive'
        ? `確定將「${contact.name}」設為離職？將從聯絡人列表隱藏，過去的報價單仍會顯示此人。`
        : `確定將「${contact.name}」恢復為在職？`
    );
    if (!confirmed) return;
    try {
      await contactsService.setStatus(contact.id, status);
      fetchContacts();
    } catch (error) {
      alert(error instanceof Error ? error.message : '更新聯絡人狀態失敗，請稍後再試');
    }
  };

  const handleDelete = async (contact: Contact) => {
    if (!window.confirm(`確定要刪除「${contact.name}」？刪除後歷史報價可能無法對到此聯絡人。若只需從列表隱藏，請改用「設為離職」。`)) {
      return;
    }
    try {
      await contactsService.delete(contact.id);
      fetchContacts();
    } catch (error) {
      alert(error instanceof Error ? error.message : '刪除聯絡人失敗，請稍後再試');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      name: '',
      email: '',
      phone: '',
      position: '',
    });
  };

  const openAddModal = () => {
    setEditingContact(null);
    resetForm();
    setShowModal(true);
  };

  const displayedContacts = showInactive
    ? contacts
    : contacts.filter((c) => isContactActive(c));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">聯絡人管理</h1>
          <p className="mt-1 text-sm text-gray-500">
            管理客戶聯絡人；設為離職會從列表隱藏，歷史報價仍可對到原聯絡人
          </p>
        </div>
        <button
          onClick={openAddModal}
          className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          新增聯絡人
        </button>
      </div>

      {/* 客戶篩選器 */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">篩選客戶：</label>
            <select
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              value={selectedCustomerId}
              onChange={(e) => setSelectedCustomerId(e.target.value)}
            >
              <option value="">全部客戶</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.company}
                </option>
              ))}
            </select>
            <label className="inline-flex items-center text-sm text-gray-700">
              <input
                type="checkbox"
                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <span className="ml-2">顯示離職</span>
            </label>
          </div>
          <div className="text-sm text-gray-500">
            共 {displayedContacts.length} 筆聯絡人
          </div>
        </div>
      </div>

      {/* 聯絡人列表 */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  姓名
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  客戶
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  電子郵件
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  狀態
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  操作
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayedContacts.map((contact) => (
                <tr key={contact.id} className={isContactActive(contact) ? 'hover:bg-gray-50' : 'hover:bg-gray-50 bg-gray-50'}>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${isContactActive(contact) ? 'text-gray-900' : 'text-gray-500'}`}>
                    {contact.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {contact.customers?.company || '未知客戶'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {contact.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span className={isContactActive(contact) ? 'text-green-700' : 'text-gray-500'}>
                      {contactStatusLabel(contact.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => handleEdit(contact)}
                        className="text-primary hover:text-primary/80"
                        title="編輯"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      {isContactActive(contact) && (
                        <button
                          onClick={() => handleSetStatus(contact, 'inactive')}
                          className="text-warning hover:text-warning/80"
                          title="設為離職"
                        >
                          <UserMinusIcon className="h-4 w-4" />
                        </button>
                      )}
                      {!isContactActive(contact) && (
                        <button
                          onClick={() => handleSetStatus(contact, 'active')}
                          className="text-primary hover:text-primary/80"
                          title="恢復在職"
                        >
                          <ArrowPathIcon className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(contact)}
                        className="text-danger hover:text-danger/80"
                        title="刪除"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 新增/編輯聯絡人 Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingContact ? '編輯聯絡人' : '新增聯絡人'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    客戶
                  </label>
                  <select
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                  >
                    <option value="">選擇客戶</option>
                    {customers.map((customer) => (
                      <option key={customer.id} value={customer.id}>
                        {customer.company}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    姓名
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    電子郵件
                  </label>
                  <input
                    type="email"
                    required
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    電話
                  </label>
                  <input
                    type="tel"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    職位
                  </label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                  />
                </div>



                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="btn-secondary"
                  >
                    取消
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingContact ? '更新' : '新增'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contacts; 